import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { z } from "zod"

import { staticEditorConfig } from "../../app/editorConfig"
import { getBaseRepositoryUrl } from "../../app/routing"

const baseUrl = getBaseRepositoryUrl()

export function getAllOngoingTasks() {
  const url = new URL("app/bulk/list-ongoing-tasks", baseUrl)

  return request(url, { okType: z.array(z.string()) })
}

export function createTask(args: CreateTaskArgs | CreateCopyToLocaleTaskArgs) {
  const url = new URL("app/bulk/create-task", baseUrl)

  const { authStrategy } = staticEditorConfig

  return request(url, {
    method: "POST",
    body: CreateTaskRequestSchema.or(CreateCopyToLocaleTaskRequestSchema).parse(
      args,
    ),
    credentials: getRequestCredentials(authStrategy),
    okType: z.object({
      taskId: z.string(),
    }),
  })
}

export function createArchiveTask(args: CreateTaskArgs["payload"]) {
  return createTask({ action: "archive", payload: args })
}

export function createDeleteTask(args: CreateTaskArgs["payload"]) {
  return createTask({ action: "delete", payload: args })
}

export function createPublishTask(args: CreateTaskArgs["payload"]) {
  return createTask({ action: "publish", payload: args })
}

export function createCopyToLocaleTask(
  args: CreateCopyToLocaleTaskArgs["payload"],
) {
  return createTask({ action: "copyToAnotherLocale", payload: args })
}

export function getTask(taskId: string) {
  const url = new URL(`app/bulk/get-task/${taskId}`, baseUrl)

  return request(url, { okType: TaskRecordSchema })
}

export async function getBrokenLinks(args: CreateTaskArgs) {
  const url = new URL("app/bulk/list-broken-links", baseUrl)

  const documentIdsInRequest = args.payload.items.map((item) => item.documentId)

  const response = await request(url, {
    method: "POST",
    body: CreateTaskRequestSchema.parse(args),
    okType: z.array(brokenLink),
  })

  // remove links already included in the requested items
  // they are already being prepared and thus won't create a broken link
  return response.filter(
    (link) => !documentIdsInRequest.includes(link.documentId),
  )
}

export async function getBulkActionsReleaseSize(releaseId: string) {
  const url = new URL(`app/bulk/release-size/${releaseId}`, baseUrl)

  return request(url, {
    okType: z.object({
      size: z.number(),
    }),
  })
}

const brokenLink = z.object({
  documentId: z.string(),
  versionId: z.string(),
  groupLangId: z.string(),
  versionStatus: z.enum(["release", "unclassified", "published", "archived"]),
  lastModifiedDate: z.number(),
  customTypeLabel: z.string(),
  authorAvatarUrl: z.string().nullish(),
  authorId: z.string(),
  authorFullname: z.string(),
  title: z.string(),
  maskLabel: z.string(),
})

export type BrokenLink = z.infer<typeof brokenLink>

/**
 * Type guard to ensure that the given task is of type task record
 * This is necessary since all tasks but "copyToAnotherLocale" returns a similar
 * task record structure where documentId is included.
 *
 * "copyToAnotherLocale" tasks instead contain a lot of locale information only
 * necessary when working with this type of items
 *
 * @param task
 * @returns boolean
 */
export function isStandardTaskRecord(
  task:
    | CreateTaskArgs["payload"]["items"][number]
    | CreateCopyToLocaleTaskArgs["payload"]["items"][number],
): task is CreateTaskArgs["payload"]["items"][number] {
  return "documentId" in task
}

const ActionSchema = z.enum([
  "publish",
  "archive",
  "delete",
  "copyToAnotherLocale",
])

export type TaskAction = z.infer<typeof ActionSchema>

// readable arguments used on client
const CreateTaskArgsSchema = z.object({
  action: ActionSchema,
  payload: z.object({
    items: z.array(
      z.object({
        documentId: z.string(),
        versionId: z.string(),
        status: z.string(),
      }),
    ),
  }),
})

type CreateTaskArgs = z.infer<typeof CreateTaskArgsSchema>

// transform readable arguments into the request arguments as expected
// by the backend API
const CreateTaskRequestSchema = CreateTaskArgsSchema.transform((args) => ({
  ...args,
  payload: {
    items: args.payload.items.map((item) => ({
      d: item.documentId,
      v: item.versionId,
      s: item.status,
    })),
  },
}))

// readable arguments used on client
const CreateCopyToLocaleTaskArgsSchema = z.object({
  action: z.literal("copyToAnotherLocale"),
  payload: z.object({
    releaseId: z.string().optional(),
    releaseName: z.string().optional(),
    items: z.array(
      z.object({
        documentGroupLangId: z.string(),
        fromLanguageId: z.string(),
        toLanguageId: z.string(),
        customTypeId: z.string(),
        status: z.string(),
        mode: z.enum(["full", "light"]),
      }),
    ),
  }),
})

type CreateCopyToLocaleTaskArgs = z.infer<
  typeof CreateCopyToLocaleTaskArgsSchema
>

// transform readable arguments into the request arguments as expected
// by the backend API
const CreateCopyToLocaleTaskRequestSchema =
  CreateCopyToLocaleTaskArgsSchema.transform((args) => ({
    ...args,
    payload: {
      ...args.payload,
      items: args.payload.items.map((item) => ({
        g: item.documentGroupLangId,
        lf: item.fromLanguageId,
        lt: item.toLanguageId,
        ct: item.customTypeId,
        s: item.status,
        m: item.mode,
      })),
    },
  }))

const TaskOrCopyTaskServerItemSchema = z
  .object({
    g: z.string(),
    lf: z.string(),
    lt: z.string(),
    ct: z.string(),
    s: z.string(),
    m: z.enum(["full", "light"]),
  })
  .or(
    z.object({
      d: z.string(),
      v: z.string(),
      s: z.string(),
    }),
  )

const ErrorSchema = z.object({
  keyCondition: z.string(),
  status: z.string(),
})

function mapServerTaskIntoClientTask(
  task: z.infer<typeof TaskOrCopyTaskServerItemSchema>,
) {
  // if task includes the property "g" then the task is a copy task item
  // only copy task items include a reference to the groupLangId of a document
  if ("g" in task) {
    return {
      documentGroupLangId: task.g,
      fromLanguageId: task.lf,
      toLanguageId: task.lt,
      customTypeId: task.ct,
      status: task.s,
      mode: task.m,
    }
  }

  return {
    documentId: task.d,
    versionId: task.v,
    status: task.s,
  }
}

// the expected task response from the server when requesting updates for a task
const TaskRecordServerSchema = z.object({
  date: z.number(),
  userId: z.string(),
  action: ActionSchema,
  commitData: z
    .object({
      releaseToCommitId: z.string().optional(),
    })
    .nullish(),
  releaseId: z.string().optional(),
  items: z.array(TaskOrCopyTaskServerItemSchema),
  failed: z.array(z.array(TaskOrCopyTaskServerItemSchema.or(ErrorSchema))),
  pendingSize: z.number(),
  isComplete: z.boolean(),
  fatalError: z.unknown().optional(),
  executionData: z
    .object({
      startDate: z.number(),
      executionArn: z.string(),
    })
    .optional(),
})

// transform the server response task record into the task record we expect on
// the client with readable names for properties
const TaskRecordSchema = TaskRecordServerSchema.transform((task) => ({
  ...task,
  // transform server task items into readable client task items
  items: z
    .array(
      CreateTaskArgsSchema.shape.payload.shape.items.element.or(
        CreateCopyToLocaleTaskArgsSchema.shape.payload.shape.items.element,
      ),
    )
    .parse(task.items.map(mapServerTaskIntoClientTask)),
  // transform server errors and task items into readable client task errors
  failed: task.failed
    .map((errorArray) =>
      // reduce error array of objects [{task}, {error}] into a merged error item
      errorArray.reduce((acc, item) => ({ ...acc, ...item }), {}),
    )
    .map((item) => {
      // ensure we got the correct object shape back with a task and
      // the accompanying error merged into one item
      const parsedItem = ErrorSchema.and(TaskOrCopyTaskServerItemSchema).parse(
        item,
      )
      const clientItem = mapServerTaskIntoClientTask(parsedItem)

      return {
        ...clientItem,
        errorCondition: parsedItem.keyCondition,
        errorStatus: parsedItem.status,
      }
    }),
}))

export type TaskRecord = z.infer<typeof TaskRecordSchema>
